<script setup lang="ts">
import type { ThirdStepProps } from './types';

import { useEvents } from '~/composables/events/useEvents';
import { usePhoneVerification } from '~/composables/usePhoneVerification/usePhoneVerification';
import { Phone } from '~/core/entities/phone';
import { MixpanelProps } from '~/core/service/mixpanel';
import yup from '~/core/validation';

import messages from './ThirdStep.locale.json';

const { trackSignAndLogin } = useEvents();
const strings = messages.pt;

const props = withDefaults(defineProps<ThirdStepProps>(), {
	startCountdownImmediately: false,
	isSubmitting: false,
	showConfirmButton: true
});

const emit = defineEmits<{
	(e: 'validated', value: string): void;
	(e: 'resend' | 'changeNumber'): void;
}>();

const { meta, defineField, handleSubmit, resetForm } = useForm({
	validationSchema: yup.object({
		code: yup.array().of(yup.number().required()).required().min(6)
	}),
	initialValues: {
		code: []
	}
});

const [validationCode] = defineField('code');

const isOTPValid = defineModel<boolean | null>('isOTPValid', {
	default: null
});

watch(
	validationCode,
	newValue => {
		const codeLength = newValue?.filter(v => v != null && v !== '').length || 0;

		if (codeLength < 6 || isOTPValid.value === false) {
			if (isOTPValid.value !== null) {
				isOTPValid.value = null;
			}
			if (isSubmittingCode.value) {
				isSubmittingCode.value = false;
			}
		}
	},
	{ deep: true, immediate: false }
);

watch(isOTPValid, value => {
	if (value !== null) {
		isSubmittingCode.value = false;
	}
});

const isSubmittingCode = ref(false);

const submitCodeInternal = handleSubmit(() => {
	if (props.isSubmitting || isSubmittingCode.value) return;

	isSubmittingCode.value = true;
	const otp = unref(validationCode).join('');
	emit('validated', otp);
});

const submitCode = useThrottleFn(submitCodeInternal, 1000, true, true);

function getFormattedPhone() {
	const instance = new Phone(props.phone);
	const code = instance.getCode();
	const formattedPhone = instance.getFormattedPhone();
	return `${code} ${formattedPhone}`;
}

const isOTPInvalid = computed<boolean>(() => isOTPValid.value === false);

const countDownElement = ref<HTMLElement | null>(null);
const countResend = ref<number>(0);

const {
	pauseCountdown,
	resumeCountdown,
	resetCountdown,
	isIntervalActive,
	countdownText
} = usePhoneVerification({
	countDownElement
});

function resend() {
	if (isIntervalActive.value) return;
	countResend.value += 1;
	addEventResendClick();

	isOTPValid.value = null;
	isSubmittingCode.value = false;

	resumeCountdown();
	resetCountdown();
	validationCode.value = [];

	emit('resend');
}

const addEventResendClick = () => {
	trackSignAndLogin.tokenResendClick({
		[MixpanelProps.ATTEMPT_COUNT]: countResend.value,
		[MixpanelProps.USER_PHONE]: props.phone
	});
};

watch(
	() => props.isActive,
	isThirdStep => {
		resetCountdown(0);

		if (isThirdStep) {
			resumeCountdown();
			validationCode.value = [];
		} else {
			pauseCountdown();
		}
	},
	{
		immediate: props.startCountdownImmediately
	}
);

onUnmounted(() => {
	resetForm();
});

const pinInput = useTemplateRef('pinInput');

whenever(
	() => props.isActive,
	async () => {
		await nextTick();

		const firstPinInput = pinInput.value?.[0];

		if (!firstPinInput) return;

		firstPinInput.$el.focus();
	},
	{ immediate: true }
);

defineExpose({
	submitCode
});
</script>

<template>
	<section class="w-full space-y-3" data-testid="third-step">
		<div class="flex items-center justify-center gap-3">
			<p>{{ getFormattedPhone() }}</p>

			<span
				class="cursor-pointer leading-normal underline decoration-primary/[0.28] underline-offset-[6px]"
				@click="emit('changeNumber')"
			>
				{{ strings.changeNumber }}
			</span>
		</div>

		<PinInput
			id="pin-input"
			v-model="validationCode"
			class="flex flex-col"
			otp
			@complete="submitCode()"
		>
			<PinInputGroup class="grid h-14 w-full grid-cols-6 gap-1 lg:gap-3">
				<PinInputInput
					v-for="(id, index) in 6"
					:key="id"
					ref="pinInput"
					data-test-id="pdp-step-otp-input"
					:index="index"
					class="w-full max-w-[50px] grid-cols-1"
					:class="{
						'border-tertiary focus:border-tertiary': isOTPInvalid,
						'border-success focus:border-success': isOTPValid
					}"
					type="text"
					inputmode="numeric"
				/>
			</PinInputGroup>
			<span v-show="isOTPInvalid" class="text-center text-xs text-tertiary">
				{{ strings.OTPErrorMessage }}
			</span>
		</PinInput>

		<Button
			v-if="showConfirmButton"
			:disabled="!meta.valid || isSubmitting"
			class="h-12"
			data-testid="submit-confirmation-button"
			@click="submitCode"
		>
			<IconLineMdLoadingTwotoneLoop v-if="isSubmitting" class="size-[1.7rem]" />

			<template v-else>
				{{ strings.confirmButton }}
			</template>
		</Button>

		<div class="w-full space-y-4">
			<div class="flex items-center justify-center gap-3">
				<p
					class="cursor-pointer leading-normal underline decoration-primary/[0.28] underline-offset-[6px]"
					:class="{
						'cursor-not-allowed! text-primary-light-2': isIntervalActive
					}"
					@click="resend"
				>
					{{ strings.resendButton }}
				</p>
				<p v-show="isActive" ref="countDownElement" class="w-14">
					{{ countdownText }}
				</p>
			</div>
		</div>
	</section>
</template>
